import { useCallback, useEffect, useRef, useState } from 'react';
import config from '../config.js';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { normalizeMultistreamStatus } from '../_helpers/multistream.js';

/** How often the feed is re-read while a consumer has it enabled. */
export const MULTISTREAM_POLL_MS = 15000;

/**
 * Whether SongShake is receiving the band's stream (ENG-763), from
 * `GET /bands/{id}/multistream/status`, re-read every `pollMs` while
 * `enabled`. The endpoint is for band members, so a consumer enables it only
 * for them.
 *
 * Returns `{ status, loaded, failed, refresh }`. `status` is the normalized
 * status or null; `loaded` is whether a read has answered, and `failed`
 * whether the latest read failed. A failed read keeps the last good status.
 * Disabling the hook forgets the status, so enabling it again never shows a
 * stale feed. `refresh()` reads now (also while disabled, to have the answer
 * before enabling) and resolves once the read has landed; enabling right
 * after such a read doesn't repeat it.
 */
export const useMultistreamStatus = (bandId, { enabled = true, pollMs = MULTISTREAM_POLL_MS } = {}) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [state, setState] = useState({ status: null, loaded: false, failed: false });
  // Only the latest read may land: an older one settling late would roll
  // the feed back.
  const requestRef = useRef(0);
  const loadedRef = useRef(false);
  const active = enabled && /^[1-9]\d*$/.test(String(bandId ?? ''));

  const refresh = useCallback(async () => {
    const request = ++requestRef.current;
    try {
      const data = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandId}/multistream/status`);
      if (request !== requestRef.current) return;
      loadedRef.current = true;
      setState({ status: normalizeMultistreamStatus(data), loaded: true, failed: false });
    } catch (_err) {
      if (request !== requestRef.current) return;
      loadedRef.current = true;
      setState((prev) => ({ ...prev, loaded: true, failed: true }));
    }
  }, [bandId]);

  useEffect(() => {
    if (!active) return undefined;
    if (!loadedRef.current) refresh();
    const timer = setInterval(refresh, pollMs);
    return () => {
      clearInterval(timer);
      requestRef.current += 1;
      loadedRef.current = false;
      setState({ status: null, loaded: false, failed: false });
    };
  }, [active, refresh, pollMs]);

  return { ...state, refresh };
};
